import { Database, Server, HardDrive, Check, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useToastStore } from '@/stores/toast-store';
import { useDialogStore } from '@/stores/dialog-store';
import { useModalStore } from '@/stores/modal-store';
import { useAppStore } from '@/stores/app-store';
import { SkeletonStatCards } from '@/components/shared/Skeleton';
import { ActionPanel } from '@/components/shared/ActionPanel';
import { buildConnectionActions } from '@/lib/actions/connection-actions';
import { buildDatabaseActions } from '@/lib/actions/database-actions';
import { getDatabaseActionDeps } from '@/lib/actions/database-deps';
import { isDatabaseVisible } from '@/lib/database-visibility';
import { isSchemaVisible } from '@/lib/schema-visibility';
import type {
  InfoViewTab, DatabaseStats, ConnectionConfig, ConnectionStatus, DatabaseInfo, SchemaMetadata,
} from '@/types';

interface InfoViewProps {
  tab: InfoViewTab;
}

function StatCard({
  label,
  value,
  title,
}: {
  label: string;
  value: string | number;
  /** Optional tooltip — useful when value is truncated. */
  title?: string;
}) {
  return (
    <div className="bg-dbgo-panel border rounded-lg p-3 min-w-0 overflow-hidden">
      <div className="text-2xs text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div
        className="text-sm text-text-primary font-mono truncate"
        title={title ?? (typeof value === 'string' ? value : undefined)}
      >
        {value}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// InfoView
// ═══════════════════════════════════════════

export function InfoView({ tab }: InfoViewProps) {
  const openTab = useWorkspaceStore((s) => s.openTab);
  const queryClient = useQueryClient();
  const setConnectionStatus = useConnectionStore((s) => s.setStatus);
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const addToast = useToastStore((s) => s.addToast);
  const openEditConnection = useModalStore((s) => s.openEditConnection);

  // Hydrate the connection record for header + actions.
  const { data: connection } = useQuery<ConnectionConfig | undefined>({
    queryKey: ['connection', tab.connectionId],
    queryFn: async () => {
      const list = await apiClient.get<ConnectionConfig[]>('/api/connections');
      return list.find((c) => c.id === tab.connectionId);
    },
  });

  const status: ConnectionStatus = activeConnections[tab.connectionId]?.status ?? 'disconnected';

  const connectMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ ok: boolean }>(`/api/connections/${tab.connectionId}/connect`),
    onMutate: () => setConnectionStatus(tab.connectionId, 'connecting'),
    onSuccess: () => {
      setConnectionStatus(tab.connectionId, 'connected');
      queryClient.invalidateQueries({ queryKey: ['schema', tab.connectionId] });
      queryClient.invalidateQueries({ queryKey: ['schema-stats', tab.connectionId] });
      queryClient.invalidateQueries({ queryKey: ['databases', tab.connectionId] });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Connection failed';
      setConnectionStatus(tab.connectionId, 'error', message);
      addToast('error', message);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ ok: boolean }>(`/api/connections/${tab.connectionId}/disconnect`),
    onSuccess: () => setConnectionStatus(tab.connectionId, 'disconnected'),
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Disconnect failed';
      addToast('error', message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete<{ ok: boolean }>(`/api/connections/${tab.connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      useWorkspaceStore.getState().closeTab(tab.id);
      addToast('success', 'Connection deleted');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Delete failed';
      addToast('error', message);
    },
  });

  const icon = tab.objectType === 'connection' ? (
    <Server size={20} className="text-accent-blue" />
  ) : tab.objectType === 'database' ? (
    <Database size={20} className="text-accent-green" />
  ) : (
    <HardDrive size={20} className="text-accent-purple" />
  );

  const subtitle = tab.objectType === 'connection'
    ? 'Connection'
    : tab.objectType === 'database'
      ? 'Database'
      : 'Schema';

  // Actions — branches by object type.
  const actions = connection && tab.objectType === 'connection'
    ? buildConnectionActions(connection, status, {
        onConnect: () => connectMutation.mutate(),
        onDisconnect: () => disconnectMutation.mutate(),
        onNewQuery: () => {
          openTab({
            type: 'query',
            id: `query:${tab.connectionId}:${Date.now()}`,
            connectionId: tab.connectionId,
            title: `Query - ${connection.name}`,
            sql: '',
          });
        },
        onReload: async () => {
          // Connection-level reload goes through the /refresh endpoint so
          // the server bypasses its schema cache and rewrites it with live
          // metadata. Then drop every derived TanStack query.
          try {
            await apiClient.post(`/api/connections/${tab.connectionId}/databases/refresh`);
          } catch (err: unknown) {
            addToast('error', err instanceof Error ? err.message : 'Reload failed');
          }
          queryClient.invalidateQueries({ queryKey: ['databases', tab.connectionId] });
          queryClient.invalidateQueries({ queryKey: ['schema', tab.connectionId] });
          queryClient.invalidateQueries({ queryKey: ['schema-stats', tab.connectionId] });
        },
        onCreateDatabase: () => useModalStore.getState().openCreateDatabase(tab.connectionId),
        onEdit: () => openEditConnection(connection),
        onRename: async () => {
          const newName = await useDialogStore.getState().showPrompt({
            title: 'Rename Connection',
            defaultValue: connection.name,
          });
          if (newName?.trim() && newName.trim() !== connection.name) {
            try {
              await apiClient.put(`/api/connections/${tab.connectionId}`, { name: newName.trim() });
              queryClient.invalidateQueries({ queryKey: ['connections'] });
            } catch (err: unknown) {
              addToast('error', err instanceof Error ? err.message : 'Rename failed');
            }
          }
        },
        onCopyHostPort: () => {
          navigator.clipboard.writeText(`${connection.host}:${connection.port}`);
          addToast('success', 'Copied to clipboard');
        },
        onShare: () => useModalStore.getState().openShareDialog?.({
          resourceType: 'connection',
          resourceId: connection.id,
          resourceName: connection.name,
        }),
        onDelete: () => {
          useDialogStore.getState().showConfirm({
            title: 'Delete Connection',
            message: `This will permanently delete the connection "${connection.name}" and all of its saved queries. The remote database itself is untouched. This action cannot be undone.`,
            confirmText: connection.name,
            danger: true,
            onConfirm: async () => {
              await new Promise<void>((resolve, reject) => {
                deleteMutation.mutate(undefined, {
                  onSuccess: () => resolve(),
                  onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
                });
              });
            },
          });
        },
      })
    : tab.objectType === 'database'
      ? buildDatabaseActions(
          getDatabaseActionDeps({
            connectionId: tab.connectionId,
            dbName: tab.objectName,
            defaultSchema: 'public',
            openTab,
            queryClient,
            onTriggerRestore: () => {
              addToast('info', 'Restore from SQL is available from the sidebar.');
            },
          }),
        )
      : null;

  return (
    <div className="h-full overflow-y-auto bg-dbgo-workarea">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header — colored icon bg + left stripe when the connection has a
            colorTag. Makes prod-vs-staging tabs instantly distinguishable
            even when the name is identical. */}
        <div
          className="flex items-center gap-3 mb-6 pl-3"
          style={{
            boxShadow: connection?.colorTag ? `inset 3px 0 0 0 ${connection.colorTag}` : undefined,
          }}
        >
          <div
            className="w-10 h-10 rounded-lg border flex items-center justify-center"
            style={{
              backgroundColor: connection?.colorTag ? `${connection.colorTag}22` : 'var(--dbgo-panel)',
              borderColor: connection?.colorTag ?? 'var(--border-default)',
            }}
          >
            {icon}
          </div>
          <div>
            <h2 className="text-base font-semibold text-text-primary">{tab.objectName}</h2>
            <p className="text-xs text-text-secondary">{subtitle}</p>
          </div>
        </div>

        {tab.objectType === 'connection' && connection && (
          <ConnectionBody
            tab={tab}
            connection={connection}
            status={status}
          />
        )}

        {tab.objectType === 'database' && (
          <DatabaseBody tab={tab} />
        )}

        {/* Actions */}
        {actions && <ActionPanel items={actions} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Connection-scoped body: driver/host cards + databases with checkboxes
// ═══════════════════════════════════════════

interface ConnectionBodyProps {
  tab: InfoViewTab;
  connection: ConnectionConfig;
  status: ConnectionStatus;
}

function ConnectionBody({ tab, connection, status }: ConnectionBodyProps) {
  const openTab = useWorkspaceStore((s) => s.openTab);
  const queryClient = useQueryClient();
  const setDatabaseVisible = useAppStore((s) => s.setDatabaseVisible);
  const clearDatabaseVisibility = useAppStore((s) => s.clearDatabaseVisibility);
  const visibilityOverrides = useAppStore((s) => s.databaseVisibility[tab.connectionId]);

  const { data: databases, isLoading: dbsLoading } = useQuery<DatabaseInfo[]>({
    queryKey: ['databases', tab.connectionId],
    queryFn: () => apiClient.get<DatabaseInfo[]>(`/api/connections/${tab.connectionId}/databases`),
    // Only fetch when the connection is actually open — prevents noise on tabs
    // that opened before the user connected.
    enabled: status === 'connected',
    staleTime: 60_000,
  });

  const refreshDatabases = async () => {
    try {
      const fresh = await apiClient.post<DatabaseInfo[]>(`/api/connections/${tab.connectionId}/databases/refresh`);
      queryClient.setQueryData(['databases', tab.connectionId], fresh);
    } catch (err: unknown) {
      useToastStore.getState().addToast('error', err instanceof Error ? err.message : 'Refresh failed');
    }
  };

  const visibleCount = (databases ?? []).filter((d) =>
    isDatabaseVisible(d, visibilityOverrides),
  ).length;

  const driverLabel: Record<string, string> = {
    postgresql: 'PostgreSQL',
    mysql: 'MySQL',
    mssql: 'SQL Server',
    oracle: 'Oracle',
    files: 'Files (DuckDB)',
    sqlite: 'SQLite',
    mongodb: 'MongoDB',
  };

  return (
    <>
      {/* Connection-level cards. Host / Port are hidden for file-backed
          connections since they don't have a network endpoint. The grid
          switches to 3 columns for those so the 4 remaining cards (Driver,
          Default DB, Status, Databases) fill neatly without orphans. */}
      <div
        className={clsx(
          'grid grid-cols-2 gap-3 mb-6',
          connection.driverType === 'files' ? 'sm:grid-cols-4' : 'sm:grid-cols-4',
        )}
      >
        <StatCard label="Driver" value={driverLabel[connection.driverType] ?? connection.driverType} />
        {connection.driverType !== 'files' && (
          <>
            <StatCard label="Host" value={connection.host || '—'} />
            <StatCard label="Port" value={connection.port ? String(connection.port) : '—'} />
          </>
        )}
        {(() => {
          // For file-backed connections, `databaseName` is the full path to a
          // per-connection .duckdb file — way too long for the card. Show a
          // friendly "Files" label and surface the actual path in the tooltip.
          const raw = connection.databaseName || '';
          if (connection.driverType === 'files') {
            return <StatCard label="Default DB" value="Files" title={raw || 'Files'} />;
          }
          return <StatCard label="Default DB" value={raw || '—'} title={raw || undefined} />;
        })()}
        <StatCard
          label="Status"
          value={
            status === 'connected' ? 'Connected'
            : status === 'connecting' ? 'Connecting…'
            : status === 'error' ? 'Error'
            : 'Disconnected'
          }
        />
        <StatCard
          label="Databases"
          value={databases ? `${visibleCount} / ${databases.length}` : '—'}
        />
      </div>

      {/* Databases list with visibility checkboxes */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
            Databases
          </h3>
          <button
            onClick={refreshDatabases}
            disabled={status !== 'connected'}
            className="text-[11px] text-accent-blue hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Refresh
          </button>
        </div>

        {status !== 'connected' && (
          <div className="text-xs text-text-muted py-3 px-3 border border-dashed rounded-md">
            Connect to see the database list.
          </div>
        )}

        {status === 'connected' && dbsLoading && (
          <div className="text-xs text-text-muted py-3 px-3">Loading…</div>
        )}

        {status === 'connected' && databases && databases.length === 0 && (
          <div className="text-xs text-text-muted py-3 px-3">No databases found.</div>
        )}

        {status === 'connected' && databases && databases.length > 0 && (
          <div className="border rounded-lg overflow-hidden divide-y divide-subtle">
            {databases.map((db) => {
              const visible = isDatabaseVisible(db, visibilityOverrides);
              const isOverridden = visibilityOverrides && db.name in visibilityOverrides;

              return (
                <div
                  key={db.name}
                  className="flex items-center gap-3 px-3 py-2 bg-dbgo-panel hover:bg-dbgo-tab-hover transition-colors"
                >
                  {/* Visibility checkbox */}
                  <button
                    onClick={() => setDatabaseVisible(tab.connectionId, db.name, !visible)}
                    title={visible ? 'Hide from sidebar tree' : 'Show in sidebar tree'}
                    className={clsx(
                      'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                      visible
                        ? 'bg-accent-blue border-accent-blue text-white'
                        : 'bg-transparent border-strong text-transparent hover:border-accent-blue',
                    )}
                  >
                    <Check size={12} strokeWidth={3} />
                  </button>

                  {/* Name + flags */}
                  <button
                    onClick={() => {
                      openTab({
                        type: 'info',
                        id: `info:db:${tab.connectionId}:${db.name}`,
                        connectionId: tab.connectionId,
                        database: db.name,
                        objectType: 'database',
                        objectName: db.name,
                      });
                    }}
                    className="flex-1 min-w-0 text-left"
                  >
                    <span className="text-sm font-mono text-text-primary truncate">{db.name}</span>
                    {db.isDefault && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-accent-blue">default</span>
                    )}
                    {db.isSystem && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-text-muted">system</span>
                    )}
                  </button>

                  {/* Reset override (only shown when user has explicitly toggled) */}
                  {isOverridden && (
                    <button
                      onClick={() => clearDatabaseVisibility(tab.connectionId, db.name)}
                      title="Reset to default visibility"
                      className="text-[10px] text-text-muted hover:text-text-primary"
                    >
                      reset
                    </button>
                  )}

                  {visible ? (
                    <Eye size={13} className="text-text-muted flex-shrink-0" />
                  ) : (
                    <EyeOff size={13} className="text-text-muted flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {databases && databases.length > 0 && (
          <p className="mt-2 text-[11px] text-text-muted">
            Only the default database is shown in the sidebar by default. Check any other
            database to pin it in the tree; your choices persist across sessions.
          </p>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════
// Database-scoped body: stats for THIS database + table list
// ═══════════════════════════════════════════

function DatabaseBody({ tab }: { tab: InfoViewTab }) {
  // Resolves which database to fetch stats for. Prefer `tab.database`;
  // fall back to `objectName` for legacy tabs opened before multi-db landed.
  const dbName = tab.database ?? tab.objectName;

  const { data: stats, isLoading, error } = useQuery<DatabaseStats>({
    queryKey: ['schema-stats', tab.connectionId, dbName],
    queryFn: () => {
      const qs = dbName ? `?database=${encodeURIComponent(dbName)}` : '';
      return apiClient.get<DatabaseStats>(`/api/schema/${tab.connectionId}/stats${qs}`);
    },
    staleTime: 60_000,
  });

  // Schema metadata — shared via TanStack Query cache with DatabaseSubtree
  // in the sidebar, so flipping a schema visibility here immediately
  // updates the sidebar without another fetch.
  const { data: schemaMeta } = useQuery<SchemaMetadata>({
    queryKey: ['schema', tab.connectionId, dbName],
    queryFn: () => apiClient.get<SchemaMetadata>(
      `/api/schema/${tab.connectionId}?database=${encodeURIComponent(dbName)}`,
    ),
    staleTime: 60_000,
  });

  const schemas = schemaMeta?.databases?.[0]?.schemas ?? [];
  const hasSchemaDefaults = schemas.some((s) => s.isDefault);

  return (
    <>
      {isLoading && <SkeletonStatCards count={6} />}

      {error && (
        <div className="text-xs text-accent-red py-4">
          {error instanceof Error ? error.message : 'Failed to load stats'}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Size" value={stats.sizeFormatted} />
          <StatCard label="Tables" value={stats.tableCount} />
          <StatCard label="Views" value={stats.viewCount} />
          <StatCard label="Functions" value={stats.functionCount} />
          <StatCard label="Encoding" value={stats.encoding} />
          <StatCard
            label="Connections"
            value={`${stats.activeConnections} / ${stats.maxConnections}`}
          />
        </div>
      )}

      {hasSchemaDefaults && (
        <SchemaPicker
          connectionId={tab.connectionId}
          dbName={dbName}
          schemas={schemas}
        />
      )}

      {stats?.tables && stats.tables.length > 0 && (
        <div className="mb-6">
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
            Tables
          </h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-dbgo-grid-header text-text-secondary">
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Schema</th>
                  <th className="text-right px-3 py-2 font-medium">Rows (est.)</th>
                  <th className="text-right px-3 py-2 font-medium">Size</th>
                  <th className="text-right px-3 py-2 font-medium">Index Size</th>
                </tr>
              </thead>
              <tbody>
                {stats.tables.map((t) => (
                  <tr
                    key={`${t.schema}.${t.name}`}
                    className="border-t border-subtle hover:bg-dbgo-grid-row-hover transition-colors"
                  >
                    <td className="px-3 py-1.5 font-mono text-text-primary">{t.name}</td>
                    <td className="px-3 py-1.5 text-text-secondary">{t.schema}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-syn-number">
                      {t.rowEstimate.toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-text-secondary">
                      {t.sizeFormatted}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-text-secondary">
                      {t.indexSizeFormatted}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════
// Schema picker — Oracle (and future drivers) exposes many schemas per
// database. We surface a checkbox list so the user chooses which schemas
// appear in the sidebar tree. Default view: only the `isDefault` schema
// (the connecting user's own). Overrides persist in app-store.
// ═══════════════════════════════════════════

function SchemaPicker({
  connectionId,
  dbName,
  schemas,
}: {
  connectionId: string;
  dbName: string;
  schemas: SchemaMetadata['databases'][0]['schemas'];
}) {
  const overrides = useAppStore((s) => s.schemaVisibility[connectionId]?.[dbName]);
  const setSchemaVisible = useAppStore((s) => s.setSchemaVisible);
  const clearSchemaVisibility = useAppStore((s) => s.clearSchemaVisibility);

  // Sort: default first, then alphabetical. Keeps "your own schema" at the
  // top even when dozens of others are visible.
  const sorted = [...schemas].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return a.name.localeCompare(b.name);
  });

  const visibleCount = sorted.filter((s) => isSchemaVisible(s, overrides)).length;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
          Schemas
        </h3>
        <span className="text-2xs text-text-muted">
          {visibleCount} of {sorted.length} visible
        </span>
      </div>
      <div className="border rounded-lg overflow-hidden divide-y divide-subtle max-h-80 overflow-y-auto">
        {sorted.map((s) => {
          const visible = isSchemaVisible(s, overrides);
          const overridden = overrides && s.name in overrides;
          return (
            <div
              key={s.name}
              className="flex items-center gap-3 px-3 py-2 bg-dbgo-panel hover:bg-dbgo-tab-hover transition-colors"
            >
              <button
                onClick={() => setSchemaVisible(connectionId, dbName, s.name, !visible)}
                title={visible ? 'Hide from sidebar tree' : 'Show in sidebar tree'}
                className={clsx(
                  'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                  visible
                    ? 'bg-accent-blue border-accent-blue text-white'
                    : 'bg-transparent border-strong text-transparent hover:border-accent-blue',
                )}
              >
                <Check size={12} strokeWidth={3} />
              </button>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-mono text-text-primary truncate">{s.name}</span>
                {s.isDefault && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-accent-blue">default</span>
                )}
                <span className="ml-2 text-[10px] text-text-muted">
                  {s.tables.length} table{s.tables.length === 1 ? '' : 's'}
                  {s.views.length > 0 && ` · ${s.views.length} view${s.views.length === 1 ? '' : 's'}`}
                </span>
              </div>
              {overridden && (
                <button
                  onClick={() => clearSchemaVisibility(connectionId, dbName, s.name)}
                  title="Reset to default visibility"
                  className="text-[10px] text-text-muted hover:text-text-primary"
                >
                  reset
                </button>
              )}
              {visible ? (
                <Eye size={13} className="text-text-muted flex-shrink-0" />
              ) : (
                <EyeOff size={13} className="text-text-muted flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
